import * as React from "react";
import {
  getRememberedAccountInitials,
  getRememberedAccountName,
  IRememberedAccount
} from "../../auth/remembered-accounts";
import { getIdentitySeed, KuikaAvatar } from "../ui-kit/kuika-avatar/kuika-avatar";
import KuikaBadge from "../ui-kit/kuika-badge/kuika-badge";
import KuikaCloseButton from "../ui-kit/kuika-close-button/kuika-close-button";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";

export interface IAccountChooserProps {
  /** Most recent first — the first row is the one marked "Last used". */
  accounts: IRememberedAccount[];
  onPick: (account: IRememberedAccount) => void;
  onRemove: (account: IRememberedAccount) => void;
  onUseAnotherAccount: () => void;
  /** Focus the first row on arrival — set when the person navigated here, not on page load. */
  autoFocus?: boolean;
}

/**
 * The accounts this device has signed in with. Picking one leads to its sign-in step; the ✕ takes
 * it off this device (the only way an account leaves the list — signing out keeps it).
 */
export const AccountChooser = ({
  accounts,
  onPick,
  onRemove,
  onUseAnotherAccount,
  autoFocus
}: IAccountChooserProps): React.ReactElement => {
  const listRef: React.RefObject<HTMLUListElement> = React.useRef<HTMLUListElement>(null);
  // Removing an account unmounts the ✕ that had focus; without this, focus would fall to <body>
  // and a keyboard user would have to find their way back into the list.
  const shouldFocusFirstRow: React.MutableRefObject<boolean> = React.useRef<boolean>(Boolean(autoFocus));

  React.useEffect((): void => {
    if (!shouldFocusFirstRow.current) {
      return;
    }
    shouldFocusFirstRow.current = false;
    listRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
  }, [accounts]);

  const remove: (account: IRememberedAccount) => void = React.useCallback(
    (account: IRememberedAccount): void => {
      shouldFocusFirstRow.current = true;
      onRemove(account);
    },
    [onRemove]
  );

  return (
    <div className="auth-page__panel" data-testid="accountChooser">
      <div className="auth-page__heading">
        <h1 className="auth-page__title">Welcome back</h1>
        <p className="auth-page__subtitle">Choose an account to continue.</p>
      </div>

      <ul className="auth-page__account-list" aria-label="Accounts on this device" ref={listRef}>
        {accounts.map((account: IRememberedAccount, index: number): React.ReactElement => (
          // The item, not the row button, carries the id: the remove button is the row's sibling,
          // so a test reaches it by scoping into the item that holds the address.
          <li key={account.email} className="auth-page__account-item" data-testid="rememberedAccountItem">
            <button
              type="button"
              className="auth-page__account-row"
              onClick={(): void => onPick(account)}
              data-testid="rememberedAccountRow"
            >
              <KuikaAvatar
                size={40}
                src={account.photoUrl}
                seed={getIdentitySeed({
                  firstName: account.firstName,
                  lastName: account.lastName,
                  username: account.email
                })}
              >
                {getRememberedAccountInitials(account)}
              </KuikaAvatar>
              <span className="auth-page__account-text">
                <span className="auth-page__account-name">{getRememberedAccountName(account)}</span>
                <span className="auth-page__account-email">{account.email}</span>
              </span>
              {index === 0 && <KuikaBadge content="Last used" size="small" color="blue" variant="subtle" />}
            </button>
            <KuikaCloseButton
              aria-label={`Remove ${account.email} from this device`}
              onClick={(): void => remove(account)}
              data-testid="removeRememberedAccountButton"
            />
          </li>
        ))}
        <li className="auth-page__account-item">
          <button
            type="button"
            className="auth-page__account-row"
            onClick={onUseAnotherAccount}
            data-testid="useAnotherAccountButton"
          >
            {/* No seed: the neutral circle an unknown person gets, the size of the avatars above. */}
            <KuikaAvatar size={40} aria-hidden="true">
              <KuikaSvg name="plus" />
            </KuikaAvatar>
            <span className="auth-page__account-text">
              <span className="auth-page__account-name">Use another account</span>
            </span>
          </button>
        </li>
      </ul>
    </div>
  );
};
